import React, { FC, ReactElement, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useHistory, useParams } from "react-router-dom";
import {
  UserOutlined,
  MailOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  ShoppingOutlined,
  ArrowLeftOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
  IdcardOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { Spin, Table } from "antd";

import { selectAdminStateUser, selectIsAdminStateLoading } from "../../../redux-toolkit/admin/admin-selector";
import { selectOrders, selectTotalElements } from "../../../redux-toolkit/orders/orders-selector";
import { fetchUserInfo } from "../../../redux-toolkit/admin/admin-thunks";
import { resetOrders } from "../../../redux-toolkit/orders/orders-slice";
import { resetAdminState } from "../../../redux-toolkit/admin/admin-slice";
import { LoadingStatus, OrderResponse, UserOrdersRequest } from "../../../types/types";
import { fetchUserOrdersByEmail } from "../../../redux-toolkit/orders/orders-thunks";
import { ACCOUNT_USER_ORDERS, ACCOUNT_ADMIN_USERS } from "../../../constants/routeConstants";
import { useTablePagination } from "../../../hooks/useTablePagination";

import "./ManageUser.css";

const STATUS_CONFIG: Record<string, { color: string; bg: string }> = {
  PENDING:    { color: "#e65100", bg: "#fff3e0" },
  CONFIRMED:  { color: "#1565c0", bg: "#e3f2fd" },
  PROCESSING: { color: "#6a1b9a", bg: "#f3e5f5" },
  SHIPPED:    { color: "#00695c", bg: "#e0f2f1" },
  DELIVERED:  { color: "#2e7d32", bg: "#e8f5e9" },
  FULFILLED:  { color: "#2e7d32", bg: "#e8f5e9" },
  CANCELLED:  { color: "#c62828", bg: "#ffebee" },
  RETURNED:   { color: "#4e342e", bg: "#efebe9" },
};

const getStatus = (s: string) =>
  STATUS_CONFIG[s?.toUpperCase()] ?? { color: "#555", bg: "#f5f5f5" };

const ManageUser: FC = (): ReactElement => {
  const dispatch    = useDispatch();
  const history     = useHistory();
  const params      = useParams<{ id: string }>();
  const userData    = useSelector(selectAdminStateUser);
  const userOrders  = useSelector(selectOrders);
  const totalElements = useSelector(selectTotalElements);
  const isLoading   = useSelector(selectIsAdminStateLoading);

  const handleTableChange = useTablePagination<OrderResponse, UserOrdersRequest>(
    fetchUserOrdersByEmail,
    userData?.email || ""
  );

  const {
    id, email, firstName, lastName,
    city, address, phoneNumber, postIndex,
    provider, roles, active,
  } = userData || {};

  useEffect(() => {
    if (params.id) dispatch(fetchUserInfo(params.id));
    return () => {
      dispatch(resetOrders());
      dispatch(resetAdminState(LoadingStatus.LOADING));
    };
  }, [params.id, dispatch]);

  useEffect(() => {
    if (userData?.email) {
      dispatch(fetchUserOrdersByEmail({ email: userData.email, page: 0 }));
    }
  }, [userData?.email, dispatch]);

  const initials = ((firstName?.[0] ?? "") + (lastName?.[0] ?? "")).toUpperCase() || "U";
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || "—";
  const roleLabel = Array.isArray(roles) ? roles.join(", ") : (roles ?? "—");

  if (isLoading) {
    return (
      <div className="mu-page">
        <div className="mu-center"><Spin size="large" /></div>
      </div>
    );
  }

  return (
    <div className="mu-page">

      {/* ── HEADER ── */}
      <div className="mu-header">
        <button className="mu-back-btn" onClick={() => history.push(ACCOUNT_ADMIN_USERS)}>
          <ArrowLeftOutlined />
        </button>
        <div className="mu-header-text">
          <span className="mu-header-title">User Profile</span>
          <span className="mu-header-sub">ID #{id ?? "—"}</span>
        </div>
        <span className={`mu-status-pill ${active ? "active" : "inactive"}`}>
          {active
            ? <><CheckCircleFilled /> Active</>
            : <><CloseCircleFilled /> Inactive</>
          }
        </span>
      </div>

      {/* ── PROFILE HERO ── */}
      <div className="mu-hero">
        <div className="mu-avatar">{initials}</div>
        <div className="mu-hero-info">
          <div className="mu-hero-name">{fullName}</div>
          <div className="mu-hero-role">{roleLabel}</div>
          {email && (
            <div className="mu-hero-email">
              <MailOutlined /> {email}
            </div>
          )}
        </div>
      </div>

      {/* ── DETAILS GRID ── */}
      <div className="mu-section-title">
        <IdcardOutlined /> Contact & Address
      </div>
      <div className="mu-details-grid">
        {[
          { icon: <PhoneOutlined />,      label: "Phone",          val: phoneNumber },
          { icon: <EnvironmentOutlined />, label: "City",           val: city },
          { icon: <EnvironmentOutlined />, label: "Address",        val: address },
          { icon: <EnvironmentOutlined />, label: "Post Index",     val: postIndex },
          { icon: <UserOutlined />,        label: "Provider",       val: provider },
        ].map(({ icon, label, val }) => (
          <div key={label} className="mu-detail-card">
            <span className="mu-detail-icon">{icon}</span>
            <div className="mu-detail-body">
              <span className="mu-detail-label">{label}</span>
              <span className="mu-detail-val">{val != null && String(val).trim() !== "" ? String(val) : "—"}</span>
            </div>
          </div>
        ))}
      </div>

      {/* ── ORDERS ── */}
      <div className="mu-section-title">
        <ShoppingOutlined /> Orders
        {userOrders.length > 0 && (
          <span className="mu-orders-count">{userOrders.length}</span>
        )}
      </div>

      {userOrders.length === 0 ? (
        <div className="mu-empty">
          <div className="mu-empty-icon">📦</div>
          <p className="mu-empty-text">No orders placed yet</p>
        </div>
      ) : (
        <>
          {/* Mobile: order cards */}
          <div className="mu-order-cards">
            {userOrders.map((order: OrderResponse) => {
              const st = getStatus(order.deliveryStatus ?? "");
              return (
                <Link
                  key={order.id}
                  to={`${ACCOUNT_USER_ORDERS}/${order.id}`}
                  className="mu-order-card"
                >
                  <div className="mu-order-card-top">
                    <div>
                      <div className="mu-order-id">Order #{order.id}</div>
                      <div className="mu-order-date">{order.date ?? "—"}</div>
                    </div>
                    <span
                      className="mu-order-badge"
                      style={{ color: st.color, background: st.bg }}
                    >
                      {order.deliveryStatus ?? "—"}
                    </span>
                  </div>
                  <div className="mu-order-card-bottom">
                    <span className="mu-order-total">
                      {order.totalPrice != null ? `₹${order.totalPrice}` : "Price N/A"}
                    </span>
                    <span className="mu-order-city">{order.city ?? order.address ?? "—"}</span>
                    <RightOutlined className="mu-order-arrow" />
                  </div>
                </Link>
              );
            })}
          </div>

          {/* Desktop: table */}
          <div className="mu-order-table">
            <Table
              rowKey="id"
              onChange={handleTableChange}
              pagination={{
                total: totalElements,
                position: ["bottomRight"],
                size: "small",
              }}
              dataSource={userOrders}
              size="small"
              columns={[
                {
                  title: "Order #",
                  dataIndex: "id",
                  key: "id",
                  render: (v) => <span style={{ fontWeight: 700 }}>#{v}</span>,
                },
                { title: "Date",       dataIndex: "date",       key: "date" },
                { title: "City",       dataIndex: "city",       key: "city" },
                { title: "Address",    dataIndex: "address",    key: "address" },
                { title: "Post Index", dataIndex: "postIndex",  key: "postIndex" },
                {
                  title: "Status",
                  dataIndex: "deliveryStatus",
                  key: "deliveryStatus",
                  render: (s: string) => {
                    const st = getStatus(s);
                    return (
                      <span style={{
                        color: st.color, background: st.bg,
                        padding: "2px 8px", borderRadius: 3,
                        fontSize: 11, fontWeight: 700,
                      }}>
                        {s ?? "—"}
                      </span>
                    );
                  },
                },
                {
                  title: "Total",
                  dataIndex: "totalPrice",
                  key: "totalPrice",
                  render: (v) => v != null ? `₹${v}` : "—",
                },
                {
                  title: "",
                  key: "actions",
                  render: (_, order: OrderResponse) => (
                    <Link
                      to={`${ACCOUNT_USER_ORDERS}/${order.id}`}
                      style={{ color: "#2874f0", fontWeight: 600, fontSize: 13 }}
                    >
                      View
                    </Link>
                  ),
                },
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ManageUser;